import $ = require('jquery');
import ModulWithRedux from './ModulWithRedux';
import l10n from '../util/l10n';
import HeadingModuleData from './heading/ModuleData';
import WebView from './heading/WebView';
import { HeadingEditViewBasics } from './heading/EditView';
import diyLightTheme = require('../material-ui/diyLightTheme');
import ModuleInlineEditor from './common/js/ModuleInlineEditor';
import { setUndoState, setRedoState } from '../module-action-bar/index';
import AppRegistry from "diy/AppRegistry";
import TabOptions from '../actionBar/interfaces/TabOptions';

function getStore() {
    return AppRegistry.getStoreProvider().getStore();
}

declare var CKEDITOR: any;

interface CommandListener {
    (this: {state: number}): void;
}

export default class ModulHeaderHeading extends ModulWithRedux<HeadingModuleData> {

    hasUndoRedo = true;

    helpArticleId = 'headline';

    protected data: HeadingModuleData;
    private ckEditorPromise: Promise<CKEditor>;
    private ckEditorPromiseResolve: (ckEditor: CKEditor) => void;
    private inlineEditor: ModuleInlineEditor = null;

    constructor(moduleId: number) {
        super(moduleId);
        this.ckEditorPromise = new Promise<CKEditor>((resolve, reject) => {
            this.ckEditorPromiseResolve = resolve;
        });
    }

    getInPlaceEditViewClass(): any {
        return null;
    }

    getWebViewClass() {
        return WebView;
    }

    getCKEditorPromise() {
        return this.ckEditorPromise;
    }

    loadData(data: HeadingModuleData) {
        this.data = data;
        this.data.header = this.getHeadingElement().innerHTML;
    }
    getHeadingElement() {
        let h = this.getContentElement().querySelector('h1');
        if (!h) {
            h = this.getContentElement().querySelector('h2');
        }
        return h;
    }
    /**
     * @override
     * strictly for typing purposes
     */
    getData() {
        return this.data;
    }

    initCKEditor(textElement: HTMLElement) {
        this.inlineEditor = new ModuleInlineEditor(this, textElement);

        this.inlineEditor.startEditing({
             // this setting is primary used for setting the z-index
            // of CKEditor dialogs, and these should be the same as
            // standard Material UI dialogs zIndex
            baseFloatZIndex: diyLightTheme.zIndex.dialog,

            'diy-moduleType': this.moduleType,
            'diy-excludePasteScrubbers': 'semanticplaceholders',
            toolbar: 'Heading',
            toolbar_Heading: this.getCkeditorToolbarConfig(),
            diyhelpLabel: 'headline',
            format_tags: 'h1;h2',
            extraPlugins: 'doNothing,lineutils,widget,diysemanticplaceholder',
            keystrokes : [
                [ 13, 'doNothing' ],
                [ CKEDITOR.SHIFT + 13, 'doNothing' ]
            ],
            autoParagraph: false,
            removeFormatAttributes : true,
            forcePasteAsPlainText: true,
            ignoreEmptyParagraph: true,
            enterMode: CKEDITOR.ENTER_BR,
            allowedContent: 'h1 h2'
            // allow semantic placeholder markup regardless of feature flag,
            // so they can survive if the flag is turned off and on again
            + ';span[!class,!data-semantic-data-key](!diysemanticplaceholder)'
            ,
            disallowedContent: 'p br pre',
            hideToolbar: true
        });

        this.inlineEditor.editor.config.protectedSource.push( /<\?[\s\S]*?\?>/g );
        this.inlineEditor.editor.on('paste', function (ev: any) {
            ev.data.dataValue = ev.data.dataValue.replace(/<br>/g, ' ');
        });
        this.inlineEditor.editor.on('saveSnapshot', () => {
            /* whenever something gets modified in the editor, we get the heading level
             based on the actual DOM (getSaveData()) and set the value inside module's data.
             this is done because the change of format (h1/h2) does not modify the data
             model of the heading module. */
            let saveData = this.getSaveData();
            if (!saveData) {
                return;
            }
            // update module data (heading level)
            this.data.h = saveData.h;
        });
        this.inlineEditor.editor.on('change', (ev: any) => {
            if (ev.editor.getData().length === 0) {
                /* when the user deletes everything from the editor, we ensure we create
                 empty tags with the correct level from the module data, which has been updated
                 using the code above, based on the DOM that CKEditor changed */
                let h = this.data.h;
                ev.editor.setData(`<h${h}>&nbsp;</h${h}>`, function() {
                    let range = ev.editor.createRange();
                    range.moveToElementEditablePosition( ev.editor.editable(), true );
                    ev.editor.getSelection().selectRanges( [ range ] );
                });
            }
        });

        this.inlineEditor.editor.on('pluginsLoaded', (ev: any) => {
            ev.editor.focus();
        });

        this.inlineEditor.editor.on('instanceReady', () => {
            this.ckEditorPromiseResolve(this.inlineEditor.getEditor());

            const store = getStore();

            this.inlineEditor.editor.getCommand('undo').on('state', function() {
                store.dispatch(setUndoState({enabled: this.state !== CKEDITOR.TRISTATE_DISABLED}));
            } as CommandListener);

            this.inlineEditor.editor.getCommand('redo').on('state', function() {
                store.dispatch(setRedoState({enabled: this.state !== CKEDITOR.TRISTATE_DISABLED}));
            } as CommandListener);

        });
    }

    getActionBarTabs(): TabOptions[] {
        return [
            {
                label: l10n('module.heading.tabs.basic'),
                content: this.getConnectedComponent(HeadingEditViewBasics)
            }
        ];
    }

    undoHandler = () => {
        this.inlineEditor.getEditor().execCommand('undo');
    }

    redoHandler = () => {
        this.inlineEditor.getEditor().execCommand('redo');
    }

    /**
     * @override
     */

    getSaveData() {
        let data = super.getSaveData();

        // nothing to do if the editor is not accessible
        if (!this.inlineEditor) {
            return data;
        }

        // init vars
        let hLevel = 1;
        let hContents = '';
        let editorContents = $.trim(this.inlineEditor.getHtmlData());

        // see if the text coming from editor is not blank
        if (editorContents) {

            // we'll work with a mini-DOM to make HTML manipulations easier
            let $miniDom = $('<foo>' + editorContents + '</foo>');

            // check that it has only one child
            if ($miniDom.children().length !== 1
                || !( // and that it's of type H1 or H2
                    $miniDom.children().first().is('h1')
                    || $miniDom.children().first().is('h2')
                )
            ) {
                // we ensure that the correct heading level from the module data is used
                let h = parseInt(this.data.h, 10);
                // if it doesn't, we create a root H1 before we continue
                $miniDom.html(`<h${h}>${editorContents}</h${h}>`);
                //XXX: this may not work in all circumstances; for example
                //if the markup already contains a H1, jquery will
                //autocorrect and not allow another wrapping one;
                //then again in some cases it will help
            } else {
                // at this point we're sure it has H1 or H2 as root element;
                // if it's an H2 we need to update the H level
                if ($miniDom.children().first().is('h2')) {
                    hLevel = 2;
                }
            }

            // extract the content of the heading
            hContents = $miniDom.children().first().html();
        } else {
            /* in case the module is saved when the text is empty, we must send the correct
            hLevel so that next time the module is edited, the same level will be applied.
            Without this, if we were on H2 and save an empty module, we would get back H1 */
            hLevel = parseInt(this.data.h, 10);
        }

        // aggregate the information and send it on
        data.h = "" + hLevel;
        data.header = hContents;
        return data;
    }
    /**
     * @override
     */
    close(userClose?: boolean, cancelEdit?: boolean, isSaving?: boolean, isUserAction?: boolean) {
        this.inlineEditor.stopEditing();
        super.close(userClose, cancelEdit, isSaving, isUserAction);
    }

    private getCkeditorToolbarConfig() {
        let {headerFormat} = this.getData().supports;
        let config: string[][] = [];

        config.push(['Undo', 'Redo']);
        if (headerFormat) {
            config.push(['Format']);
        }
        config.push(['StyleButton']);
        config.push(['DiySemanticPlaceholder']);
        config.push(['HelpButton']);
        config.push(['CloseButton']);

        return config;
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/ModulHeaderHeading.ts
 **/